/**
 * Parse and transform source-grounded routine output for WorkChat.
 *
 * Compliance-sensitive routines emit inline `[n]` markers plus a trailing
 * Sources section. Parsing is opt-in: we only transform messages that include
 * a Sources block with at least one valid `[n] label` reference line. Valid
 * grounded output is turned into Streamdown-safe `<source-cite>` elements and a
 * structured reference list for footer chips and popovers.
 *
 * Only user-visible sources (consult notes, transcripts, documents, etc.)
 * activate citation chrome. Internal tool/query labels are stripped. Orphan
 * inline markers with no matching Sources entry are removed rather than
 * rendered as empty chips.
 *
 * While streaming: hide the Sources tail, suppress inline markers until their
 * Sources entry resolves, and defer the footer until the stream completes.
 */

import type {
  AssistantTextItem,
  ChatItem,
  ToolUseItem,
} from '../../contracts/harness.types';
import { rewriteOutsideMarkdownFences } from '../markdown-fences';
import {
  isValidSourceLabel,
  parseSourceGroundedLabel,
  type ParsedSourceGroundedLabel,
  type SourceGroundedSourceKind,
} from './source-grounded-label';

export interface SourceGroundedReference {
  id: number;
  label: string;
  /** How this reference reached the client; only render_sources output is backend-verified. */
  provenance?: 'render_sources';
  /** Unparsed label text from the Sources line — used for noise filtering. */
  rawLabel?: string;
  /** Optional quote or excerpt after an em dash in the Sources list. */
  detail?: string;
  kind?: SourceGroundedSourceKind;
  title?: string;
  subtitle?: string;
  sessionId?: string;
  documentId?: string;
  patientId?: string;
  memoryId?: string;
  workflowId?: string;
  templateId?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isRenderSourceKind = (
  value: unknown
): value is Extract<SourceGroundedSourceKind, 'consult_note' | 'transcript'> =>
  value === 'consult_note' || value === 'transcript';

const parseRenderSourcesData = (data: unknown): SourceGroundedReference[] => {
  if (!isRecord(data) || !Array.isArray(data.source_citations)) return [];

  const references: SourceGroundedReference[] = [];
  for (const citation of data.source_citations) {
    if (!isRecord(citation)) continue;
    const { marker, kind, label, quote, session_id: sessionId } = citation;
    if (
      typeof marker !== 'number' ||
      !Number.isInteger(marker) ||
      marker < 1 ||
      !isRenderSourceKind(kind) ||
      typeof label !== 'string' ||
      typeof quote !== 'string' ||
      typeof sessionId !== 'string'
    ) {
      continue;
    }
    references.push({
      id: marker,
      label,
      provenance: 'render_sources',
      rawLabel: label,
      detail: quote,
      kind,
      title: label,
      sessionId,
    });
  }
  return dedupeReferences(references);
};

export const sourceReferencesFromRenderSourcesData = (
  data: unknown
): SourceGroundedReference[] => parseRenderSourcesData(data);

const RENDER_SOURCES_TOOL_NAME = 'render_sources';
const EMPTY_SOURCE_REFERENCES: SourceGroundedReference[] = [];

const parseJsonLoose = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    const start = value.indexOf('{');
    const end = value.lastIndexOf('}');
    if (start === -1 || end <= start) return undefined;
    try {
      return JSON.parse(value.slice(start, end + 1));
    } catch {
      return undefined;
    }
  }
};

const resultDataCandidates = (item: ToolUseItem): Record<string, unknown>[] => {
  const candidates: Record<string, unknown>[] = [];
  const metadataData = item.result_provider_metadata?.data;
  if (isRecord(metadataData)) candidates.push(metadataData);

  const content = item.result?.content;
  if (isRecord(content)) {
    candidates.push(content);
    if (isRecord(content.data)) candidates.push(content.data);
  } else if (typeof content === 'string') {
    const parsed = parseJsonLoose(content);
    if (isRecord(parsed)) {
      candidates.push(parsed);
      if (isRecord(parsed.data)) candidates.push(parsed.data);
    }
  }
  return candidates;
};

export const renderSourcesReferencesForAssistant = (
  previousItemsById: ReadonlyMap<string, ChatItem> | undefined,
  assistant: AssistantTextItem
) => {
  const previous = previousItemsById?.get(assistant.id);
  if (
    previous?.kind !== 'tool_use' ||
    previous.name !== RENDER_SOURCES_TOOL_NAME ||
    previous.status !== 'complete'
  ) {
    return EMPTY_SOURCE_REFERENCES;
  }

  for (const data of resultDataCandidates(previous)) {
    const references = sourceReferencesFromRenderSourcesData(data);
    if (references.length > 0) return references;
  }
  return EMPTY_SOURCE_REFERENCES;
};

export interface PreparedSourceGroundedContent {
  body: string;
  references: SourceGroundedReference[];
  /** Parsed refs resolved for inline chips — may exist while footer is deferred. */
  inlineReferences: SourceGroundedReference[];
}

const INLINE_CITATION_RE = /(?<!\])\*{0,2}\[\s*(\d+(?:\s*,\s*\d+)*)\s*\]\*{0,2}/g;

/** Map letter-suffixed markers like [1a] to their parent numeric citation [1]. */
const INLINE_SUB_CITATION_RE = /(?<!\])\*{0,2}\[\s*(\d+)[a-zA-Z]+\s*\]\*{0,2}/g;

/** Incomplete `[` while streaming — strip so raw brackets never flash. */
const DANGLING_CITATION_PREFIX_RE = /\[(?:\s*\d+(?:\s*,\s*\d*)*(?:[a-zA-Z]+)?)?$/;

const maskMarkdownFences = (text: string): string =>
  rewriteOutsideMarkdownFences(
    text,
    (prose) => prose,
    (fence) => fence.replace(/[^\r\n]/g, ' ')
  );

const SOURCES_HEADING_RE =
  /(?:^|\n)(?:#{1,3}\s*)?(?:\*{1,2})?(?:Sources|References)(?:\*{1,2})?:?\s*\n+/i;

const REFERENCE_LINE_PREFIX_RE = /^(?:[-*•>\u2022]\s*)?(?:\d+\.\s+)?/;

const REFERENCE_LINE_RE =
  /^(?:[-*•>\u2022]\s*)?(?:\d+\.\s+)?(?:\*\*)?\[(\d+)\](?:\*\*)?\s*:?\s*(.+?)\s*$/;

const SOURCES_TABLE_HEADER_RE = /source\s*type/i;

const isSourcesTableHeaderRow = (trimmed: string): boolean =>
  SOURCES_TABLE_HEADER_RE.test(trimmed) &&
  /quote\s*\/?\s*label|date\s*\/?\s*session/i.test(trimmed);

const isSourcesTableSeparatorRow = (trimmed: string): boolean => {
  const withoutPipes = trimmed.replace(/\|/g, '').trim();
  return /^:?-{3,}(?:\s*:?-{3,})+$/.test(withoutPipes);
};

const splitSourcesTableCells = (trimmed: string): string[] | null => {
  if (trimmed.includes('|')) {
    const cells = trimmed
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim());
    return cells.some(Boolean) ? cells : null;
  }
  if (trimmed.includes('\t')) {
    const cells = trimmed.split('\t').map((cell) => cell.trim());
    return cells.some(Boolean) ? cells : null;
  }
  return null;
};

const stripWrappedQuotes = (value: string): string =>
  value.replace(/^["“'`]([\s\S]*?)["”'`]$/, '$1').trim();

/** Turn `24 Aug 2026 — Session A ("Cough, Hemoptysis, BP, Mole")` into a cite label. */
const normalizeSourcesTableSessionColumn = (value: string): string => {
  const letterAlias = value.match(
    /^(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s+\d{4})?)\s*[—–-]\s*Session\s+[A-Z]\s*\(\s*["'](.+?)["']\s*\)\s*$/i
  );
  if (letterAlias?.[1] && letterAlias[2]) {
    return `${letterAlias[2].trim()}, ${letterAlias[1].trim()}`;
  }
  return value.trim();
};

/** Compliance models often emit Sources as pipe/tab tables — normalize to `[n] label — "quote"`. */
const normalizeSourcesReferenceLine = (line: string): string | null => {
  const trimmed = line.trim();
  if (!trimmed) return null;
  if (isSourcesTableHeaderRow(trimmed) || isSourcesTableSeparatorRow(trimmed)) {
    return null;
  }

  const cells = splitSourcesTableCells(trimmed);
  if (cells && cells.length >= 3) {
    const idCell = cells[0] ?? '';
    const idMatch =
      /^\[(\d+)\]$/.exec(idCell) ??
      /^\*\*\[(\d+)\]\*\*$/.exec(idCell) ??
      /^(\d+)$/.exec(idCell);
    if (!idMatch?.[1]) return null;

    const sourceType = stripWrappedQuotes(cells[1] ?? 'Consult note') || 'Consult note';
    const dateSession = normalizeSourcesTableSessionColumn(cells[2] ?? '');
    const label = `${sourceType}, ${dateSession}`.replace(/,\s*,/g, ',').trim();
    const quote = cells[3] ? stripWrappedQuotes(cells[3]) : '';
    return quote ? `[${idMatch[1]}] ${label} — "${quote}"` : `[${idMatch[1]}] ${label}`;
  }

  const match = REFERENCE_LINE_RE.exec(trimmed);
  if (match?.[1] && match[2]) {
    return `[${match[1]}] ${match[2]}`;
  }
  return null;
};

/** Strip list/quote/number prefixes before classifying a Sources line. */
const stripSourcesLinePrefix = (trimmed: string): string =>
  trimmed.replace(REFERENCE_LINE_PREFIX_RE, '');

/** Nested sub-citation lines (for example "- **[1a]** — …" or bare "[1a] — …"). */
const isSourcesNestedLine = (trimmed: string): boolean =>
  /^(?:\*\*)?\[\d+[a-zA-Z]+\](?:\*\*)?(?:\s|$|[—–]|-|:)/.test(
    stripSourcesLinePrefix(trimmed)
  );

/** Horizontal rules between Sources entries — skip, do not treat as trailing prose. */
const isHorizontalRuleLine = (trimmed: string): boolean =>
  /^(-{3,}|\*{3,}|_{3,})$/.test(trimmed);

const normalizeSubCitationMarkers = (text: string): string =>
  rewriteOutsideMarkdownFences(text, (prose) =>
    prose.replace(INLINE_SUB_CITATION_RE, '[$1]')
  );

/** Internal tool / query labels — not user-navigable, so skip citation chrome. */
const NON_ACTIONABLE_SOURCE_LABEL_RE =
  /(?:task\s*list\s*query|list_sessions|list_tasks|get_tasks|get_consult_note|get_transcript|get_session_context|get_patient_summary|searched\s+sessions|session\s*search|(?:internal\s+)?tool\s+(?:call|output|result)|\btask\s*query\b|query\s+results?|search\s+results?)/i;

const EXACT_TOOL_NOISE_RE = /^(?:query|search|tool)$/i;

/** Agent-audit strings that must not become clinician-facing citation chips. */
const NON_ACTIONABLE_SOURCE_DETAIL_RE =
  /(?:focus term not found|\(focus term|\(none\s*[—–-]\s*preview mode\))/i;

const isActionableRawSourceLabel = (label: string): boolean => {
  const trimmed = label.trim();
  return (
    !NON_ACTIONABLE_SOURCE_LABEL_RE.test(trimmed) && !EXACT_TOOL_NOISE_RE.test(trimmed)
  );
};

const splitDetail = (rest: string): { label: string; detail?: string } => {
  const trimmed = rest.trim();
  // Only split on em dash when a quoted excerpt follows — not for metadata chains
  // like `session "…" — 21 Aug 2026 — patient linked to profile …`.
  const quoted = trimmed.match(/^(.+?)\s+[—–-]\s+(["“'])([\s\S]*)\2\s*$/);
  if (quoted?.[1] && quoted[3]) {
    const detail = quoted[3].trim();
    return { label: quoted[1].trim(), detail: detail || undefined };
  }
  return { label: trimmed };
};

const enrichReference = (
  ref: Omit<
    SourceGroundedReference,
    | 'kind'
    | 'title'
    | 'subtitle'
    | 'sessionId'
    | 'documentId'
    | 'patientId'
    | 'memoryId'
    | 'workflowId'
    | 'templateId'
  >
): SourceGroundedReference => {
  const parsed: ParsedSourceGroundedLabel = parseSourceGroundedLabel(ref.label);
  return {
    ...ref,
    label: parsed.displayLabel,
    kind: parsed.kind,
    title: parsed.title,
    subtitle: parsed.subtitle,
    sessionId: parsed.sessionId,
    documentId: parsed.documentId,
    patientId: parsed.patientId,
    memoryId: parsed.memoryId,
    workflowId: parsed.workflowId,
    templateId: parsed.templateId,
  };
};

/** Parse `[n] label — "quote"` lines from a Sources block. */
export const parseSourceGroundedReferences = (raw: string): SourceGroundedReference[] => {
  const refs: SourceGroundedReference[] = [];
  for (const line of raw.split('\n')) {
    const normalized = normalizeSourcesReferenceLine(line);
    if (!normalized) continue;
    const match = REFERENCE_LINE_RE.exec(normalized);
    if (!match?.[1] || !match[2]) continue;
    const id = Number(match[1]);
    if (!Number.isFinite(id)) continue;
    const { label, detail } = splitDetail(match[2]);
    if (!label) continue;
    const normalizedDetail = detail ? normalizeSourceDetail(detail) : undefined;
    refs.push(
      enrichReference({
        id,
        label,
        rawLabel: label,
        detail: normalizedDetail || undefined,
      })
    );
  }
  return dedupeReferences(refs);
};

/** Keep the first entry per id; prefer later duplicates that carry navigation ids. */
const dedupeReferences = (refs: SourceGroundedReference[]): SourceGroundedReference[] => {
  const byId = new Map<number, SourceGroundedReference>();
  for (const ref of refs) {
    const existing = byId.get(ref.id);
    if (!existing) {
      byId.set(ref.id, ref);
      continue;
    }
    const existingScore =
      Number(Boolean(existing.sessionId)) +
      Number(Boolean(existing.documentId)) +
      Number(Boolean(existing.detail));
    const refScore =
      Number(Boolean(ref.sessionId)) +
      Number(Boolean(ref.documentId)) +
      Number(Boolean(ref.detail));
    if (refScore > existingScore) {
      byId.set(ref.id, ref);
    }
  }
  return [...byId.values()].sort((a, b) => a.id - b.id);
};

/** Non-numeric bracket refs (for example "[Evidence A]") are not source-grounded cites. */
const isNonNumericBracketRefLine = (trimmed: string): boolean =>
  /^(?:\*\*)?\[[^\d\]]/.test(stripSourcesLinePrefix(trimmed));

const SOURCES_HEADING_GLOBAL_RE = new RegExp(SOURCES_HEADING_RE.source, 'gi');

const isSourcesHeading = (heading: string): boolean =>
  /(?:Sources)(?:\*{1,2})?:?\s*\n+$/i.test(heading);

const isSourcesTailJunkLine = (trimmed: string): boolean =>
  isNonNumericBracketRefLine(trimmed) ||
  Boolean(normalizeSourcesReferenceLine(trimmed)) ||
  REFERENCE_LINE_RE.test(trimmed) ||
  isSourcesNestedLine(trimmed) ||
  isHorizontalRuleLine(trimmed) ||
  isSourcesTableHeaderRow(trimmed) ||
  isSourcesTableSeparatorRow(trimmed);

interface ParsedSourcesSection {
  start: number;
  headingLength: number;
  references: SourceGroundedReference[];
  trailing: string;
  prefersSourcesHeading: boolean;
}

const parseSourcesSectionAt = (
  text: string,
  start: number,
  headingLength: number,
  prefersSourcesHeading: boolean
): ParsedSourcesSection => {
  const afterHeading = text.slice(start + headingLength);
  const lines = afterHeading.split('\n');

  const refLineTexts: string[] = [];
  let trailingStartIndex = lines.length;

  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i]?.trim() ?? '';
    if (!trimmed) continue;

    if (isSourcesTableHeaderRow(trimmed) || isSourcesTableSeparatorRow(trimmed)) {
      continue;
    }

    const normalized = normalizeSourcesReferenceLine(trimmed);
    if (normalized) {
      refLineTexts.push(normalized);
      continue;
    }

    if (isSourcesNestedLine(trimmed)) continue;
    if (isNonNumericBracketRefLine(trimmed)) {
      if (refLineTexts.length > 0) {
        trailingStartIndex = i;
        break;
      }
      continue;
    }
    if (isHorizontalRuleLine(trimmed)) continue;

    if (refLineTexts.length > 0) {
      trailingStartIndex = i;
      break;
    }
  }

  const references = parseSourceGroundedReferences(refLineTexts.join('\n'));
  const trailingLines = lines.slice(trailingStartIndex);
  while (
    trailingLines.length > 0 &&
    isSourcesTailJunkLine(trailingLines[0]?.trim() ?? '')
  ) {
    trailingLines.shift();
  }
  const trailing = trailingLines.join('\n').trim();

  return {
    start,
    headingLength,
    references,
    trailing,
    prefersSourcesHeading,
  };
};

const scoreSourcesSection = (section: ParsedSourcesSection): number => {
  let score = section.references.length * 100;
  if (section.prefersSourcesHeading) score += 1_000;
  score += section.start;
  return score;
};

/** Scan every Sources/References block — Evidence tool "References" often precedes the real Sources list. */
const findBestSourcesSection = (text: string): ParsedSourcesSection | null => {
  let best: ParsedSourcesSection | null = null;
  const searchableText = maskMarkdownFences(text);

  for (const match of searchableText.matchAll(SOURCES_HEADING_GLOBAL_RE)) {
    if (match.index == null) continue;
    const section = parseSourcesSectionAt(
      text,
      match.index,
      match[0].length,
      isSourcesHeading(match[0])
    );
    if (section.references.length === 0) continue;
    if (!best || scoreSourcesSection(section) >= scoreSourcesSection(best)) {
      best = section;
    }
  }

  return best;
};

const stripStreamingSourcesTail = (text: string): string => {
  let lastIndex: number | null = null;
  for (const match of maskMarkdownFences(text).matchAll(SOURCES_HEADING_GLOBAL_RE)) {
    if (match.index != null) lastIndex = match.index;
  }
  if (lastIndex == null) return text;
  return text.slice(0, lastIndex).trimEnd();
};

const extractSourcesSection = (
  text: string
): { body: string; references: SourceGroundedReference[] } => {
  const section = findBestSourcesSection(text);
  if (!section) {
    return { body: text.trim(), references: [] };
  }

  const before = text.slice(0, section.start).trimEnd();
  const body = section.trailing ? `${before}\n\n${section.trailing}`.trim() : before;

  return { body, references: section.references };
};

const citationIdsFromMatch = (raw: string): number[] =>
  raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);

/** Merge adjacent `[1][2]` markers so both ids render after cite-tag injection. */
const coalesceAdjacentCitationMarkers = (text: string): string => {
  return rewriteOutsideMarkdownFences(text, (prose) => {
    let prev = '';
    let current = prose;
    while (prev !== current) {
      prev = current;
      current = current.replace(
        /(\[\s*\d+(?:\s*,\s*\d+)*\s*\])(\[\s*\d+(?:\s*,\s*\d+)*\s*\])/g,
        (_match, first: string, second: string) => {
          const ids = [
            ...citationIdsFromMatch(first.slice(1, -1)),
            ...citationIdsFromMatch(second.slice(1, -1)),
          ];
          return `[${ids.join(', ')}]`;
        }
      );
    }
    return current;
  });
};

export const isActionableSourceLabel = (label: string): boolean => {
  const trimmed = label.trim();
  return isValidSourceLabel(trimmed) && isActionableRawSourceLabel(trimmed);
};

export const filterActionableReferences = (
  references: SourceGroundedReference[]
): SourceGroundedReference[] => references.filter((ref) => isActionableReference(ref));

const isActionableReference = (ref: SourceGroundedReference): boolean => {
  const rawLabel = ref.rawLabel ?? ref.label;
  const labelForCheck = ref.title ?? ref.label;
  const detail = ref.detail?.trim();
  const knownClinicalKind = ref.kind != null && ref.kind !== 'unknown';
  return (
    isActionableRawSourceLabel(rawLabel) &&
    (knownClinicalKind || isValidSourceLabel(labelForCheck)) &&
    !NON_ACTIONABLE_SOURCE_LABEL_RE.test(ref.label) &&
    !NON_ACTIONABLE_SOURCE_LABEL_RE.test(labelForCheck) &&
    !EXACT_TOOL_NOISE_RE.test(labelForCheck) &&
    !(detail && NON_ACTIONABLE_SOURCE_DETAIL_RE.test(detail))
  );
};

const stripInlineCitationMarkers = (text: string): string =>
  rewriteOutsideMarkdownFences(text, (prose) => prose.replace(INLINE_CITATION_RE, ''));

/** Replace `[n]` / `[1, 2]` markers with `<source-cite>` for Streamdown. */
export const injectSourceCiteTags = (
  text: string,
  allowedIds?: ReadonlySet<number>
): string =>
  rewriteOutsideMarkdownFences(text, (prose) =>
    prose.replace(INLINE_CITATION_RE, (_match, ids: string) => {
      const parsed = citationIdsFromMatch(ids);
      const resolved =
        allowedIds == null ? parsed : parsed.filter((id) => allowedIds.has(id));
      if (resolved.length === 0) return '';
      const label = resolved.join('·');
      return `<source-cite data-idx="${resolved.join(',')}">${label}</source-cite>`;
    })
  );

/**
 * Markdown table parsers can drop custom elements when a cell contains ONLY a
 * citation chip. Prefix an invisible joiner so Streamdown keeps the element.
 */
export const stabilizeLonelyTableCellCites = (text: string): string => {
  const prefixLonelyCite = (cell: string): string =>
    cell.replace(/^\s*(<source-cite\s[^>]*>[\s\S]*?<\/source-cite>)\s*$/, ' \u200B$1 ');

  return text
    .split('\n')
    .map((line) => {
      if (!line.includes('|') || !line.includes('<source-cite')) return line;
      return line
        .split('|')
        .map((cell) => prefixLonelyCite(cell))
        .join('|');
    })
    .join('\n');
};

/** Strip markdown emphasis wrappers models often put around quoted source excerpts. */
const META_DETAIL_PREFIX_RE =
  /^(?:patient[\s-]facing(?:\s+summary)?\s+)?(?:note\s+)?format\s*[:;]\s*/i;

const META_DETAIL_TAIL_RE =
  /\s*(?:;\s*)?(?:no\s+(?:allergy|consent|patient|identifier|name|profile)[\s\S]*|under\s+Your\s+medications[\s\S]*|not\s+(?:found|linked|documented|present)[\s\S]*|original\s+version\s+(?:omits|lacks|missing)[\s\S]*)$/i;

const VERSION_COMPARE_DETAIL_RE =
  /^(?:edited|original)\s+version\b|;\s*original\s+version\s+/i;

const extractEmbeddedClinicalQuote = (text: string): string | null => {
  const single = /['']([^'']{8,})['']/.exec(text);
  if (single?.[1]) return single[1].trim();
  const double = /"([^"]{8,})"/.exec(text);
  if (double?.[1]) return double[1].trim();
  return null;
};

export const normalizeSourceDetail = (detail: string): string => {
  let text = detail
    .replace(/^\*+([\s\S]*?)\*+$/, '$1')
    .replace(/^_+([\s\S]*?)_+$/, '$1')
    .replace(/^["“]([\s\S]*?)["”]$/, '$1')
    .trim();

  if (META_DETAIL_PREFIX_RE.test(text)) {
    text =
      extractEmbeddedClinicalQuote(text) ??
      text.replace(META_DETAIL_PREFIX_RE, '').trim();
  } else if (/patient[\s-]facing/i.test(text)) {
    text = extractEmbeddedClinicalQuote(text) ?? text;
  }

  text = text.replace(META_DETAIL_TAIL_RE, '').trim();

  if (VERSION_COMPARE_DETAIL_RE.test(text)) {
    text = extractEmbeddedClinicalQuote(text) ?? '';
  }

  if (/^(?:no|not)\s+(?:found|linked|documented|present)\b/i.test(text)) {
    return '';
  }

  if (/^same clinical presentation\b/i.test(text)) {
    return '';
  }

  if (/^patient[\s-]facing(?:\s+summary)?\s+format$/i.test(text)) {
    return '';
  }

  return text;
};

export const prepareSourceGroundedContent = (
  text: string | undefined,
  opts?: { streaming?: boolean; structuredReferences?: SourceGroundedReference[] }
): PreparedSourceGroundedContent => {
  const empty = { body: '', references: [], inlineReferences: [] };
  if (!text) return empty;

  const isStreaming = opts?.streaming ?? false;
  let working = text;
  if (isStreaming) {
    working = rewriteOutsideMarkdownFences(working, (prose) =>
      prose.replace(DANGLING_CITATION_PREFIX_RE, '')
    );
  }

  const { body: withoutSources, references } = extractSourcesSection(working);
  const structuredReferences = opts?.structuredReferences ?? [];
  const actionableReferences = filterActionableReferences(
    structuredReferences.length > 0 ? structuredReferences : references
  );

  if (actionableReferences.length === 0) {
    const hadParsedSources = references.length > 0;
    let body = hadParsedSources
      ? withoutSources
      : isStreaming
        ? stripStreamingSourcesTail(working)
        : working;
    if (isStreaming || hadParsedSources) {
      body = stripInlineCitationMarkers(normalizeSubCitationMarkers(body));
    }
    return { body: body.trim(), references: [], inlineReferences: [] };
  }

  const allowedIds = new Set(actionableReferences.map((ref) => ref.id));
  const normalizedBody = coalesceAdjacentCitationMarkers(
    normalizeSubCitationMarkers(withoutSources)
  );
  const body = stabilizeLonelyTableCellCites(
    injectSourceCiteTags(normalizedBody, allowedIds)
  );

  return {
    body,
    references: isStreaming ? [] : actionableReferences,
    inlineReferences: actionableReferences,
  };
};

export const referencesById = (
  references: SourceGroundedReference[]
): Map<number, SourceGroundedReference> =>
  new Map(references.map((ref) => [ref.id, ref]));

export const scrollToSourceCitation = (id: number): void => {
  if (typeof document === 'undefined') return;
  const target = String(id);
  const el = Array.from(
    document.querySelectorAll<HTMLElement>('[data-source-cite-id]')
  ).find((node) => {
    const raw = node.getAttribute('data-source-cite-id');
    if (!raw) return false;
    return raw.split(',').some((part) => part.trim() === target);
  });
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  el.classList.add('ring-2', 'ring-validation-info', 'ring-offset-1');
  window.setTimeout(() => {
    el.classList.remove('ring-2', 'ring-validation-info', 'ring-offset-1');
  }, 1200);
};
